import { useState, useEffect, lazy, Suspense } from "react";
import { Layout } from "./components/Layout";
import { Modal } from "shared/components";
import { useMakeDecisionController } from "./controller";
import { Views } from "./types";
import type { FormData, FormDataErrors } from "./types";
import { MakeDecisionView } from "./views/MakeDecisionView";
import { DecisionSettingsView } from "./views/DecisionSettingsView";
import { ConditionsView } from "./views/ConditionsView";
import { EmailView } from "./views/EmailView";
import api from "api/landlord/address";
import { SuccessView } from "./views/SuccessView";
import { getErrorMessage } from "utils/error";
import { toast } from "react-toastify";
import { Application, DecisionAction } from "shared/types";
import { useQueryClient } from "@tanstack/react-query";
import { Loader } from "shared/components/Loader";
import { useAuthContext } from "providers/Auth";
import { Template } from "types/landlord/verificationTemplate";
import { Applicant } from "types/application";
import { activityLogsQueryKey } from "shared/queries/useActivityLogsController";
import { useSettingsController } from "views/settings-integration/controller";
import { ProspectsCreationOptions } from "views/settings-integration/integrations/pms/constants";

const ResidencyConditionsView = lazy(
  () => import("./views/ResidencyConditionsView")
);

const getFullAddress = (
  address:
    | (Application & {
        verification_template: Template;
        score: number;
      })
    | undefined
) => {
  if (!address) return "";
  const parts = [
    address.unit.title ? `${address.unit.title}, ` : "",
    address.property?.address.address1
      ? `${address.property.address.address1}, `
      : "",
    address.property?.address.city ? `${address.property.address.city} ` : "",
    address.property?.address.state_or_province
      ? `${address.property.address.state_or_province}, `
      : "",
    address.property?.address.postal_code ?? ""
  ].join("");

  return parts || "--";
};

type ControllerReturn = ReturnType<typeof useMakeDecisionController>;
type ApplicationMeta = ControllerReturn["applicationMeta"];
type Flow = ControllerReturn["flow"];

interface ViewContentProps {
  applicationMeta: ApplicationMeta;
  view: Views;
  onClose: () => void;
  formData: FormData;
  flow: Flow;
  updateFormData: (updates: Partial<FormData>) => void;
  sendApplicantsToPMS: Record<string, boolean>;
  disablePmsForOtherDecisions: boolean;
  showDetailedConditionsView: boolean;
  setShowDetailedConditionsView: React.Dispatch<React.SetStateAction<boolean>>;
  formDataErrors: FormDataErrors;
  successViewFormData: FormData;
  applicationId: string;
}

const ViewContent = ({
  applicationMeta,
  view,
  onClose,
  formData,
  flow,
  updateFormData,
  sendApplicantsToPMS,
  disablePmsForOtherDecisions,
  showDetailedConditionsView,
  setShowDetailedConditionsView,
  formDataErrors,
  successViewFormData,
  applicationId
}: ViewContentProps) => {
  if (applicationMeta.isFetching)
    return (
      <div className="h-full w-full flex items-center justify-center">
        <Loader />
      </div>
    );

  switch (view) {
    case Views.MakeDecision:
      return (
        <MakeDecisionView
          onClose={onClose}
          applicationId={applicationMeta.data?.id}
          requiresManualExecution={
            applicationMeta.data?.requires_manual_or_conditional_execution
          }
          recommendationMethod={
            applicationMeta.data?.verification_template.recommendation_method
          }
          applicants={applicationMeta.data?.applicants || []}
          recommendation={applicationMeta.data?.recommendation}
          templateCriteriaSettings={{
            conditional_approval_range_end: Number(
              applicationMeta.data?.verification_template.screening_criteria
                ?.conditional_approval_range_end
            ),
            decline_range_end: Number(
              applicationMeta.data?.verification_template.screening_criteria
                ?.decline_range_end
            )
          }}
          sendApplicantsToPMS={sendApplicantsToPMS}
          formData={formData}
          score={Number(applicationMeta.data?.score)}
          view={view}
          flow={flow}
          updateFormData={updateFormData}
        />
      );
    case Views.DecisionSettings:
      return (
        <DecisionSettingsView
          formData={formData}
          updateFormData={updateFormData}
          view={view}
          flow={flow}
          address={getFullAddress(applicationMeta.data)}
          disableAddApplicantsToPms={
            disablePmsForOtherDecisions ||
            (!applicationMeta.data?.property?.pms_id &&
              !applicationMeta.data?.property?.external_id)
          }
          unlistUnitTiming={
            applicationMeta.data?.verification_template
              .automatically_unlist_unit_timing
          }
        />
      );
    case Views.Conditions:
      return (
        <ConditionsView
          formData={formData}
          updateFormData={updateFormData}
          view={view}
          flow={flow}
          adverseActionTemplate={
            applicationMeta.data?.verification_template
              .adverse_action_letter_type
          }
          recommendation={applicationMeta.data?.recommendation}
          showDetailedConditionsView={showDetailedConditionsView}
          setShowDetailedConditionsView={setShowDetailedConditionsView}
          ruleReasons={applicationMeta.data?.rule_reasons}
          recommendationMethod={
            applicationMeta.data?.verification_template.recommendation_method
          }
          ruleCriteria={
            applicationMeta.data?.verification_template.rule_criteria
          }
        />
      );
    case Views.ResidencyConditions:
      return (
        <Suspense
          fallback={
            <div className="flex justify-center items-center h-full">
              <Loader />
            </div>
          }
        >
          <ResidencyConditionsView
            formData={formData}
            updateFormData={updateFormData}
            view={view}
            flow={flow}
            formDataErrors={formDataErrors}
          />
        </Suspense>
      );
    case Views.Email:
      return (
        <EmailView
          property={applicationMeta.data?.property}
          verificationTemplate={applicationMeta.data?.verification_template}
          applicants={applicationMeta.data?.applicants || []}
          address={getFullAddress(applicationMeta.data)}
          conditionalApprovalEmailMessage={
            applicationMeta.data?.conditional_approval_email_message_rendered
          }
          formData={formData}
          updateFormData={updateFormData}
          view={view}
          flow={flow}
        />
      );
    case Views.Success:
      return (
        <SuccessView
          formData={successViewFormData}
          address={getFullAddress(applicationMeta.data)}
          applicationId={applicationId}
          applicants={
            (applicationMeta.data?.applicants as unknown[] | undefined as
              | Applicant[]
              | undefined) || []
          }
        />
      );
    default:
      return null;
  }
};

export const MakeDecisionModal = ({
  opened,
  onClose,
  applicationId
}: {
  opened: boolean;
  onClose: () => void;
  applicationId: string;
}) => {
  const { type } = useAuthContext();
  const queryClient = useQueryClient();
  const {
    applicationMeta,
    formData,
    view,
    onNextView,
    setPrevView,
    flow,
    updateFormData,
    resetConditions,
    validateFormData,
    formDataErrors
  } = useMakeDecisionController(applicationId);
  const { currentSettings } = useSettingsController();
  const { pms: pmsSettings } = currentSettings;

  const [successViewFormData, setSuccessViewFormData] = useState(formData);
  const [showDetailedConditionsView, setShowDetailedConditionsView] =
    useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (showDetailedConditionsView)
      resetConditions(
        applicationMeta.data?.rule_reasons,
        applicationMeta.data?.recommendation
      );
  }, [showDetailedConditionsView]);

  const onSubmit = async () => {
    if (!validateFormData()) return;

    setIsSubmitting(true);
    const conditions = formData.conditions;
    if (formData.additionalCondition)
      conditions.push(formData.additionalCondition);

    const body = {
      send_applicants_to_pms: type.isPMS
        ? formData.addApplicantsToPMS
        : undefined,
      note_message: formData.decisionNotes,
      email_message: formData.additionalEmailText,
      skip_sending_email: !formData.sendNotifications
    };

    (formData.decision === DecisionAction.Approve ||
    formData.decision === DecisionAction.ConditionalApprove
      ? api.approveApplication(applicationId, {
          ...body,
          conditions:
            formData.decision === DecisionAction.Approve
              ? undefined
              : conditions
        })
      : api.rejectApplication(applicationId, {
          ...body,
          reasons: conditions
        })
    )
      .then(() => {
        const unitMagicLink = applicationMeta.data?.unit.magic_link;

        if (
          formData.decision !== DecisionAction.Decline &&
          (formData.unlistUnit || formData.disallowApplications) &&
          unitMagicLink?.id &&
          (unitMagicLink.active || applicationMeta.data?.unit.listed)
        ) {
          const newActiveStatus = formData.disallowApplications
            ? false
            : unitMagicLink.active;
          const newListedStatus = formData.unlistUnit
            ? false
            : Boolean(applicationMeta.data?.unit.listed);

          return api.updateMagicLink(unitMagicLink.id, {
            active: newActiveStatus,
            listed: newListedStatus
          });
        }

        return Promise.resolve();
      })
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: activityLogsQueryKey(applicationId)
        });
        queryClient.invalidateQueries({
          queryKey: ["APPLICATION", applicationId]
        });

        if (formData.decision === DecisionAction.Decline) onClose();
        else {
          setSuccessViewFormData(formData);
          onNextView();
        }
      })
      .catch((err) => {
        toast.error(getErrorMessage(err));
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const disablePmsForOtherDecisions = [
    ProspectsCreationOptions.Submission,
    ProspectsCreationOptions.Start
  ].includes(
    pmsSettings.pms_prospect_creation_trigger as ProspectsCreationOptions
  );

  const sendApplicantsToPMS = {
    [DecisionAction.Approve]:
      !disablePmsForOtherDecisions &&
      Boolean(pmsSettings.pms_prospect_creation_on_approve),

    [DecisionAction.ConditionalApprove]:
      !disablePmsForOtherDecisions &&
      Boolean(
        Boolean(pmsSettings.pms_prospect_creation_on_conditional_approve)
      ),

    [DecisionAction.Decline]:
      !disablePmsForOtherDecisions &&
      Boolean(pmsSettings.pms_prospect_creation_on_decline)
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      panelClassName="max-h-[840px] h-full max-w-[880px] w-full animate-appear border border-alpha-100 shadow-lg"
    >
      <Layout
        view={view}
        onClose={onClose}
        onNext={onNextView}
        onPrev={setPrevView}
        formData={formData}
        onSubmit={onSubmit}
        showDetailedConditionsView={showDetailedConditionsView}
        setShowDetailedConditionsView={setShowDetailedConditionsView}
        recommendation={applicationMeta.data?.recommendation}
        recommendationMethod={
          applicationMeta.data?.verification_template.recommendation_method
        }
        formDataErrors={formDataErrors}
        isButtonLoading={isSubmitting}
      >
        <ViewContent
          applicationMeta={applicationMeta}
          view={view}
          onClose={onClose}
          formData={formData}
          flow={flow}
          updateFormData={updateFormData}
          sendApplicantsToPMS={sendApplicantsToPMS}
          disablePmsForOtherDecisions={disablePmsForOtherDecisions}
          showDetailedConditionsView={showDetailedConditionsView}
          setShowDetailedConditionsView={setShowDetailedConditionsView}
          formDataErrors={formDataErrors}
          successViewFormData={successViewFormData}
          applicationId={applicationId}
        />
      </Layout>
    </Modal>
  );
};
